import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmationModal from './ConfirmationModal';
import { reportsApi } from '../../services/financeApi';
import { openBlob } from '../../utils/reportBlob';

/**
 * "Print Cheque" for any payment paid (partly) by cheque — prints on the bank account's cheque
 * layout (Finance → Cheque Print Layouts). Shown only when the payment has cheque lines.
 * - several cheque lines → small picker
 * - already printed → confirmation modal with the print count before reprinting
 *
 * Props:
 *   payment     — the payment (needs _id and status)
 *   sourceType  — 'NormalPayment' | 'BillPayment' | 'PettyCashReimbursement' | 'SupplierPayment'
 *   lines       — [{ index, chequeNumber, amount, chequeDate, ledgerAccountId }] cheque lines of the payment
 *   payee       — SupplierPayment only: name printed on the cheque
 *   company     — letterhead info passed to the report (optional)
 *   variant     — 'icon' (tables / cards) | 'button' (view screens)
 */
const BLOCKED = {
  NormalPayment: ['REJECTED', 'DELETED', 'CANCELLED'],
  BillPayment: ['REJECTED', 'CANCELLED'],
  PettyCashReimbursement: ['REJECTED', 'DELETED'],
  SupplierPayment: ['CANCELLED'],
};

const money = (n) => `Rs ${parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ChequePrintAction = ({ payment, sourceType, lines = [], payee, company = {}, variant = 'icon', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { line, count }
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [open]);

  const chequeLines = lines.filter(l => l && l.chequeNumber);
  if (!payment || chequeLines.length === 0) return null;

  const status = String(payment.status || '').toUpperCase();
  const blocked = (BLOCKED[sourceType] || []).includes(status);
  const paymentId = payment._id || payment.id;

  const doPrint = async (line) => {
    setBusy(true);
    try {
      const res = sourceType === 'SupplierPayment'
        ? await reportsApi.renderChequeReport({
            sourceType, paymentId, methodIndex: line.index, bankAccountId: line.ledgerAccountId,
            payee, amount: line.amount, chequeNumber: line.chequeNumber, chequeDate: line.chequeDate,
            status: payment.status, company,
          })
        : await reportsApi.getChequeReport(sourceType, paymentId, { methodIndex: line.index, company });
      openBlob(res.data.base64);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate cheque print');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  // Reprint needs confirmation
  const startPrint = async (line) => {
    setOpen(false);
    let count = 0;
    try {
      const res = await reportsApi.getChequePrintHistory(sourceType, paymentId, line.index);
      count = res.data?.data?.count || 0;
    } catch { /* print anyway */ }
    if (count > 0) setConfirm({ line, count });
    else doPrint(line);
  };

  const onClick = (e) => {
    e.stopPropagation();
    if (blocked || busy) return;
    if (chequeLines.length === 1) startPrint(chequeLines[0]);
    else setOpen(v => !v);
  };

  const title = blocked ? `Cannot print cheque — payment is ${status.toLowerCase()}` : 'Print cheque';
  const icon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 15h4M14 15h4" />
    </svg>
  );

  return (
    <div className={`relative inline-block ${className}`} ref={ref} onClick={(e) => e.stopPropagation()}>
      {variant === 'button' ? (
        <button type="button" onClick={onClick} disabled={blocked || busy} title={title}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
          {icon}{busy ? 'Printing…' : chequeLines.length > 1 ? 'Print Cheque ▾' : 'Print Cheque'}
        </button>
      ) : (
        <button type="button" onClick={onClick} disabled={blocked || busy} title={title}
          className="p-1 rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed">
          {icon}
        </button>
      )}

      {open && (
        <div className="absolute right-0 z-30 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 text-left">
          {chequeLines.map((line) => (
            <button key={line.index} type="button" onClick={() => startPrint(line)}
              className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
              Cheque #{line.chequeNumber} — {money(line.amount)}
            </button>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={() => confirm && doPrint(confirm.line)}
        loading={busy}
        type="warning"
        title="Print Cheque Again"
        message={confirm ? `Cheque #${confirm.line.chequeNumber} has already been printed ${confirm.count} time(s). Do you want to print it again?` : ''}
        confirmText="Print Again"
        confirmButtonClass="bg-indigo-600 hover:bg-indigo-700 focus:ring-indigo-500"
      />
    </div>
  );
};

// Cheque lines of a finance payment (Normal / Bill payment) — paymentMethods with method CHEQUE
export const chequeLinesOf = (payment) => (payment?.paymentMethods || [])
  .map((m, index) => ({ index, method: m.method, chequeNumber: m.chequeNumber, amount: m.amount, chequeDate: m.chequeDate, ledgerAccountId: m.ledgerAccount?._id || m.ledgerAccount }))
  .filter(m => m.method === 'CHEQUE');

// Petty cash reimbursement: one optional cheque
export const reimbursementChequeLines = (r) => (r?.chequeNo
  ? [{ index: 0, chequeNumber: r.chequeNo, amount: r.reimbursementAmount, chequeDate: r.chequeDate, ledgerAccountId: r.sourceAccountId?._id || r.sourceAccountId }]
  : []);

// Inventory supplier payment: paymentModes with paymentMethod 'cheque'
export const supplierPaymentChequeLines = (p) => (p?.paymentModes || [])
  .map((m, index) => ({ index, method: m.paymentMethod, chequeNumber: m.chequeDetails?.chequeNo, amount: m.amount, chequeDate: m.chequeDetails?.chequeDate, ledgerAccountId: m.ledgerAccount?._id || m.ledgerAccount }))
  .filter(m => m.method === 'cheque');

export default ChequePrintAction;
